import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import usePlayback, { binarySearchFirstAfter } from '../../bot/hooks/usePlayback'
import useFormingBar from '../../bot/hooks/useFormingBar'
import useScrubProjection from '../../bot/hooks/useScrubProjection'
import { TICK_REPLAY_SPEEDS } from '../../bot/hooks/useBacktestChartState'
import type { ExtendResult } from '../../bot/hooks/useGraphCandleWindow'
import type { BacktestChartHandle } from '../../bot/components/BacktestChart'
import type { IndexRange } from '../../bot/components/klineViewport'
import type { CandleData } from '../../bot/types/chartData'
import type { FormingBarState } from '../../bot/utils/formingBarBuffer'
import { parseProgressTimestamp } from '../../bot/utils/timeParsing'
import { getCandleCountForTimeframe } from '../../utils/chartUtils'
import type { CanvasSeries, CutoffFrame } from './canvasSeries'
import { buildsFormingBar, dwellMs, WALKTHROUGH_ENGINE_SPEED } from './walkthroughPacing'

/** Right gap after the newest bar, px. */
export const REPLAY_RIGHT_PAD_PX = 72
/** How often a playing replay checks whether the user moved the view away from the playhead. */
export const AUTOSCROLL_CHECK_MS = 250
/** Quiet time after the user's last scroll or zoom before the view snaps back, so a fling can finish. */
export const AUTOSCROLL_SETTLE_MS = 200

/** Gap in bars (+0.5 for setIndexRange's half bar); the user's once they set one. */
function rightPadBars(series: CanvasSeries | null | undefined): number {
  const barSpace = series?.getBarSpace() ?? 0
  const gap = series?.getRightGap() ?? REPLAY_RIGHT_PAD_PX
  return barSpace > 0 ? gap / barSpace + 0.5 : 0
}

const EMPTY: CandleData[] = []
const noSubscribe = () => () => {}
const zero = () => 0

export interface UseCanvasReplayOptions {
  btid: string
  series: CanvasSeries | null
  /** The run's symbol. The replay only ever cuts that symbol's chart. */
  runSymbol: string
  /** The canvas's live symbol and timeframe. */
  symbol: string
  timeframe: string
  /** Where the replay starts (0%), unix seconds — the drawings window's start. */
  startTs: number
  /**
   * Where it ends (100%), unix seconds — the drawings window's end, not the run's: the trail
   * after it is shown, not played, once the replay is complete.
   */
  endTs: number
  /** The cursor saved on the row, if any. */
  initialProgressView: string | null
  /** Start playing on its own once the cursor's bars are on the chart. Read once. */
  autoPlayOnReady: boolean
  /** The walkthrough is shown. Hidden, the cutoff lifts and the replay pauses. */
  shown: boolean
  /** Wall-clock milliseconds per bar, or REAL_TIME_BAR_MS. */
  barMs: number
  /**
   * True while the user is pressing or dragging on the chart — a drawing tool mid-gesture must
   * not have the view pulled out from under it by the follow loop.
   */
  holdViewport?: () => boolean
  /**
   * The forming bar was repainted, and the sub-bar cursor with it — for the trade overlay, so a
   * fill part-way through the bar shows its arrow before the bar closes.
   */
  onFormingReveal?: (
    revealTs: number | undefined,
    forming: FormingBarState | null,
    barProgress?: number,
  ) => void
}

/**
 * The walkthrough's bar replay, on the canvas chart the user draws on.
 *
 * The engine pieces are the replay chart's own, unchanged: usePlayback moves the cursor one bar
 * per dwell and persists it to the row, and useFormingBar animates the bar being revealed; while
 * playing, the view follows the playhead (see Follow below). What differs is where the bars
 * come from and go to: the canvas's CanvasSeries, which holds the loaded window (with bars
 * buffered past the cursor) and shows only up to the cutoff this hook sets.
 *
 * While `active` — shown, on the run's symbol, and not yet at the end — the series is cut at the
 * cursor. Reaching the end, or hiding the walkthrough, lifts the cutoff: the canvas shows every
 * candle again and the run stays on it as static information.
 */
export default function useCanvasReplay({
  btid,
  series,
  runSymbol,
  symbol,
  timeframe,
  startTs,
  endTs,
  initialProgressView,
  autoPlayOnReady,
  shown,
  barMs,
  holdViewport,
  onFormingReveal,
}: UseCanvasReplayOptions) {
  const seriesRef = useRef(series)
  const endTsRef = useRef(endTs)
  const holdViewportRef = useRef(holdViewport)
  const onFormingRevealRef = useRef(onFormingReveal)
  useLayoutEffect(() => {
    seriesRef.current = series
    endTsRef.current = endTs
    holdViewportRef.current = holdViewport
    onFormingRevealRef.current = onFormingReveal
  })

  // Re-render on every change to the series' data: a bar revealed, a page landing, a reload.
  const version = useSyncExternalStore(series?.subscribe ?? noSubscribe, series?.getVersion ?? zero)

  const [cursorTs, setCursorTs] = useState(() => {
    const saved = parseProgressTimestamp(initialProgressView)
    return saved == null ? startTs : Math.max(startTs, Math.min(endTs, saved))
  })
  const cursorRef = useRef(cursorTs)
  useLayoutEffect(() => {
    cursorRef.current = cursorTs
  }, [cursorTs])

  const atEnd = cursorTs >= endTs
  const active = shown && symbol === runSymbol && !!series && !atEnd
  const dwell = dwellMs(barMs, timeframe)

  // ---- The engine's view of the bars ---------------------------------------------------
  // Every loaded bar up to the run's end. Clipping only the TAIL keeps an index here equal to
  // the chart's; and running out of bars at the end is what makes the play loop ask for more,
  // be told 'exhausted', and park the cursor on the end (usePlayback) — so the run stops there
  // even though the canvas has bars beyond it.
  const clipRef = useRef<{ src: CandleData[] | null; out: CandleData[] }>({ src: null, out: EMPTY })
  const candlesRef = useMemo(
    () => ({
      get current(): CandleData[] {
        const src = seriesRef.current?.getCandles() ?? EMPTY
        if (clipRef.current.src !== src) {
          const n = binarySearchFirstAfter(src, endTsRef.current)
          clipRef.current = { src, out: n >= src.length ? src : src.slice(0, n) }
        }
        return clipRef.current.out
      },
    }),
    [],
  ) as React.RefObject<CandleData[]>
  // A finished run: nothing to wait for.
  const maxAvailableTsRef = useRef<number | null>(null)

  const extendWindow = useCallback(async (direction: 'left' | 'right'): Promise<ExtendResult> => {
    const s = seriesRef.current
    // History to the left is the canvas's own paging.
    if (!s || direction === 'left') return 'exhausted'
    const all = s.getCandles()
    if (all.length > 0 && all[all.length - 1].time > endTsRef.current) return 'exhausted'
    return s.extendRight()
  }, [])

  const loadWindow = useCallback(async (centerDate: Date) => {
    const s = seriesRef.current
    if (!s) return
    const ts = Math.floor(centerDate.getTime() / 1000)
    if (s.covers(ts)) return
    await s.loadAround(ts)
  }, [])

  // ---- The chart handle useFormingBar and the follow loop drive --------------------------
  const handle = useMemo<BacktestChartHandle>(
    () => ({
      setVisibleLogicalRange(range: IndexRange) {
        const s = seriesRef.current
        if (!s) return
        const n = s.getAppliedCount()
        let next = range
        // The canvas keeps the user's zoom, so a range only says WHERE to look: at the live edge
        // that is its newest bar; anywhere else its centre (a trade row asks for a window centred
        // on the trade, sized to a screen this chart is not holding).
        if (range.to < n - 1) {
          const v = s.getVisibleBarCount()
          if (v != null) {
            const to = Math.round((range.from + range.to) / 2 + v / 2)
            next = { from: to - v, to }
          }
        }
        s.setIndexRange(next, rightPadBars(s))
      },
      getVisibleLogicalRange: () => seriesRef.current?.getIndexRange() ?? null,
      getCandleCount: () => seriesRef.current?.getAppliedCount() ?? 0,
      getVisibleBarCount: () => seriesRef.current?.getVisibleBarCount() ?? null,
      setFormingBar(candle: FormingBarState | null, revealTs?: number, barProgress?: number) {
        seriesRef.current?.setFormingBar(candle)
        onFormingRevealRef.current?.(revealTs, candle, barProgress)
      },
    }),
    [],
  )
  const chartRef = useRef<BacktestChartHandle | null>(handle)

  // ---- Follow ----------------------------------------------------------------------------
  // While playing the view always returns to the playhead at the chevron's gap, like MT5's
  // autoscroll on a live chart, even after the user scrolls; their zoom is kept. Never while
  // they are holding the chart.
  const userMovedAtRef = useRef<number | null>(null)
  useEffect(
    () =>
      series?.onUserMove(() => {
        userMovedAtRef.current = performance.now()
      }),
    [series],
  )
  const frameEdge = useCallback(
    (to: number, fallbackSpan: number) => {
      const span = handle.getVisibleBarCount() ?? fallbackSpan
      handle.setVisibleLogicalRange({ from: Math.max(0, to - span), to })
      userMovedAtRef.current = null
    },
    [handle],
  )
  const followVisibleRange = useCallback(
    (range: { from: number; to: number }) => {
      if (holdViewportRef.current?.()) return
      frameEdge(range.to, range.to - range.from)
    },
    [frameEdge],
  )

  const playback = usePlayback({
    btid,
    rangeStartTs: startTs,
    rangeEndTs: endTs,
    progressViewTs: cursorTs,
    onProgressViewChange: setCursorTs,
    timeframe,
    // Nominal: every timing decision comes from the bar-interval override.
    speed: WALKTHROUGH_ENGINE_SPEED,
    candlesRef,
    maxAvailableTsRef,
    extendWindow,
    loadWindow,
    applyVisibleRange: followVisibleRange,
    replayStartTs: startTs,
    barIntervalMsOverride: dwell,
  })
  const { isPlaying } = playback

  // ---- The cutoff ------------------------------------------------------------------------
  // BEFORE useFormingBar, on purpose: on a scrub back, useFormingBar's own layout effect clears
  // the forming bar, and by then the cutoff has already re-applied without it — so the clear is
  // a no-op rather than a second wholesale re-apply of the canvas.
  const frameNextRef = useRef<CutoffFrame>('edge')
  const wasActiveRef = useRef(false)
  useLayoutEffect(() => {
    if (!series) return
    if (!active) {
      if (wasActiveRef.current) {
        // COMPLETE, not hidden: the move that ended the run — the last bar of the window, or a
        // scrub or step to 100% — is revealed like any other move first, so the window's last
        // candle lands where the playhead shows a new bar. The lift then keeps it on that pixel
        // and only brings the rest into view. Skipped when the end is not loaded yet: the
        // reload around it frames it itself, and cutting now would move the view twice.
        const completing = atEnd && shown && symbol === runSymbol
        if (completing && series.covers(endTs)) {
          series.setCutoff(endTs, 'follow', rightPadBars(series), runSymbol)
        }
        // Hidden (or left for another symbol) mid-run: a window reload the replay asked for is no
        // longer wanted, and landing late it would swap the canvas back to the replay's bars.
        // Completion keeps it — the reload around the end is what frames the run's last bar.
        if (!completing) series.cancelWindowLoad()
        series.setCutoff(null, 'follow', rightPadBars(series))
      }
      wasActiveRef.current = false
      frameNextRef.current = 'edge'
      return
    }
    const frame: CutoffFrame = wasActiveRef.current ? frameNextRef.current : 'edge'
    wasActiveRef.current = true
    frameNextRef.current = 'follow'
    // Also runs on every data change (`version`), which is what re-asserts a cutoff a reload
    // for another symbol dropped. Cheap when nothing moved.
    if (frame === 'follow' && series.getCutoff() === cursorTs) return
    if (frame === 'edge') series.zoomInForReplay()
    series.setCutoff(cursorTs, frame, rightPadBars(series), runSymbol)
  }, [series, active, cursorTs, runSymbol, version, atEnd, shown, symbol, endTs])
  // Unmounted while cutting (the run replaced, the dock gone): give the canvas its candles back.
  // A LAYOUT cleanup, so it runs before a replacing dock's own layout effect sets its cutoff —
  // a passive one would run after it and lift the new cutoff instead.
  useLayoutEffect(
    () => () => {
      if (wasActiveRef.current) {
        seriesRef.current?.cancelWindowLoad()
        seriesRef.current?.setCutoff(null, 'follow', rightPadBars(seriesRef.current))
      }
    },
    [],
  )

  // Restore the user's zoom when the run ends or is hidden.
  useLayoutEffect(() => {
    if (!active) series?.restoreUserZoom()
  }, [active, series])
  useLayoutEffect(() => () => seriesRef.current?.restoreUserZoom(), [])

  const formingBar = useFormingBar({
    enabled: active && buildsFormingBar(dwell),
    symbol,
    timeframe,
    speed: WALKTHROUGH_ENGINE_SPEED,
    isPlaying,
    progressViewTs: cursorTs,
    candlesRef,
    maxAvailableTsRef,
    chartRef,
    tickReplaySpeeds: TICK_REPLAY_SPEEDS,
    barIntervalMsOverride: dwell,
  })

  // ---- Transport ---------------------------------------------------------------------------
  const isPlayingRef = useRef(false)
  useLayoutEffect(() => {
    isPlayingRef.current = isPlaying
    // Play snaps the view back to the playhead at once, at the user's zoom and the chevron's gap.
    if (!isPlaying) return
    const n = handle.getCandleCount()
    if (n > 0 && seriesRef.current?.getCutoff() != null && !holdViewportRef.current?.()) frameEdge(n - 1, 0)
  }, [isPlaying, handle, frameEdge])

  // A scroll or zoom between reveals (a real-time bar can last an hour) snaps back too, once the
  // user has let go and the gesture has settled.
  useEffect(() => {
    if (!active || !isPlaying) return undefined
    const id = setInterval(() => {
      const movedAt = userMovedAtRef.current
      if (movedAt == null || holdViewportRef.current?.()) return
      if (performance.now() - movedAt < AUTOSCROLL_SETTLE_MS) return
      const n = handle.getCandleCount()
      if (n > 0) frameEdge(n - 1, 0)
    }, AUTOSCROLL_CHECK_MS)
    return () => clearInterval(id)
  }, [active, isPlaying, handle, frameEdge])

  const pauseRef = useRef(playback.pause)
  const playRef = useRef(playback.play)
  useLayoutEffect(() => {
    pauseRef.current = playback.pause
    playRef.current = playback.play
  })

  // Hidden, on another symbol, or finished: nothing is playing on the canvas any more.
  useLayoutEffect(() => {
    if (!active && isPlayingRef.current) pauseRef.current()
  }, [active])

  // A timeframe change reloads the canvas around the cursor; the run waits for it.
  const prevTimeframeRef = useRef(timeframe)
  useLayoutEffect(() => {
    if (prevTimeframeRef.current === timeframe) return
    prevTimeframeRef.current = timeframe
    if (isPlayingRef.current) pauseRef.current()
  }, [timeframe])

  // Queued play: at mount (autoPlayOnReady) or from Replay. Fires once the cursor's bars are on
  // the chart; a pause, a scrub or a step drops it.
  const [autoplayPending, setAutoplayPending] = useState(autoPlayOnReady)
  const ready = active && !!series && series.covers(cursorTs) && series.getAppliedCount() > 0
  useEffect(() => {
    if (!autoplayPending || !ready) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot signal: clearing the flag IS the effect's purpose
    setAutoplayPending(false)
    playRef.current()
  }, [autoplayPending, ready])

  const handlePlay = useCallback(() => {
    if (cursorRef.current >= endTsRef.current) return
    playback.play()
  }, [playback])

  const handlePause = useCallback(() => {
    setAutoplayPending(false)
    playback.pause()
  }, [playback])

  const handleTogglePlay = useCallback(() => {
    if (isPlayingRef.current) handlePause()
    else handlePlay()
  }, [handlePause, handlePlay])

  /** Play again from the top: lands on the start, pinned to the edge, and queues its own play. */
  const handleReset = useCallback(() => {
    frameNextRef.current = 'edge'
    playback.reset()
    setAutoplayPending(true)
  }, [playback])

  /** Frame the view on the newest revealed bar at `ts`, the way the play loop does. */
  const frameAt = useCallback(
    (ts: number) => {
      const candles = candlesRef.current ?? EMPTY
      if (candles.length === 0) return
      const idx = Math.max(0, Math.min(candles.length - 1, binarySearchFirstAfter(candles, ts) - 1))
      const visible = getCandleCountForTimeframe(timeframe, idx + 1)
      handle.setVisibleLogicalRange({ from: Math.max(0, idx - visible + 1), to: idx })
    },
    [candlesRef, handle, timeframe],
  )

  /** Scrub to an absolute time: reveal to there, and take the viewport with it. Pauses. */
  const handleScrubTime = useCallback(
    (ts: number) => {
      setAutoplayPending(false)
      playback.jumpTo(ts, { keepWindow: true })
      frameAt(Math.max(startTs, ts))
    },
    [playback, frameAt, startTs],
  )

  /** One bar either way, paused. A step past the last bar of the run ends it. */
  const handleStep = useCallback(
    (delta: 1 | -1) => {
      const candles = candlesRef.current ?? EMPTY
      if (candles.length === 0) return
      const next = binarySearchFirstAfter(candles, cursorRef.current)
      setAutoplayPending(false)
      if (delta > 0 && next >= candles.length) {
        playback.jumpTo(endTsRef.current, { keepWindow: true })
        return
      }
      const idx = Math.max(0, Math.min(candles.length - 1, delta > 0 ? next : next - 2))
      playback.jumpTo(candles[idx].time, { keepWindow: true })
      frameAt(candles[idx].time)
    },
    [candlesRef, playback, frameAt],
  )

  /**
   * Bring a past moment into view WITHOUT moving the playhead — a trade row's "show me". The
   * replay keeps its place: looking at a trade is not the same act as winding the run back to
   * it. Pages the history in first if the moment is older than what the canvas holds.
   */
  const handleShowTime = useCallback(
    async (ts: number) => {
      if (isPlayingRef.current) handlePause()
      const s = seriesRef.current
      if (!s) return
      await s.ensureFrom(ts)
      const candles = s.getCandles()
      const n = s.getAppliedCount()
      if (n === 0) return
      const idx = Math.max(0, Math.min(n - 1, binarySearchFirstAfter(candles, ts) - 1))
      const half = Math.floor((s.getVisibleBarCount() ?? 40) / 2)
      // Held off the live edge so setVisibleLogicalRange centres it rather than pinning.
      handle.setVisibleLogicalRange({ from: idx - half, to: Math.min(idx + half, n - 2) })
    },
    [handlePause, handle],
  )

  // ---- Readouts ----------------------------------------------------------------------------
  const progressPosition = Math.max(0, Math.min(1, (cursorTs - startTs) / Math.max(1, endTs - startTs)))
  const scrub = useScrubProjection({
    rangeStartTs: startTs,
    rangeEndTs: endTs,
    maxAvailableTs: null,
    progressViewPosition: progressPosition,
    onScrubTime: handleScrubTime,
  })

  // The newest bar the chart shows: the playhead's bar while cut, the canvas's last once lifted.
  const appliedCount = series?.getAppliedCount() ?? 0
  const lastApplied = appliedCount > 0 ? series!.getCandles()[appliedCount - 1] : null

  return {
    /** The canvas is cut at the cursor right now. */
    active,
    cursorTs,
    isPlaying,
    atEnd,
    /** Close of the newest bar on the chart, for floating P/L. */
    markPrice: lastApplied?.close ?? null,
    /** Newest closed bar on the chart, unix seconds. */
    lastAppliedTs: lastApplied?.time ?? null,
    version,
    scrub,
    handlePlay,
    handlePause,
    handleTogglePlay,
    handleReset,
    handleStep,
    handleScrubTime,
    handleShowTime,
    tickReplay: { active: formingBar.active, available: formingBar.available },
  }
}
